import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { Button, Card, Input, Select } from "@nutrayan/ui";
import { apiClient } from "@/lib/api-client";
import {
  createProduct,
  createProductSchema,
  type CreateProductValues,
  type ProductView,
} from "@/lib/catalog-api";
import { ProductFields } from "./ProductFields";

// Create: POST /admin/api/v1/catalog/products then variants + publish. Shares the
// product-body fields + schema with the Edit flow via ProductFields / catalog-api.

const variantSchema = z.object({
  sku: z.string().regex(/^[A-Z]{3}-[A-Z]{3}-\d{3}(-[A-Z0-9]+)?$/, "Format: NUT-SHK-001"),
  name: z.string().min(2),
  priceMrpPaise: z.coerce.number().int().positive("MRP in paise, e.g. 129900"),
  priceSellingPaise: z.coerce.number().int().positive(),
  priceSubscriptionPaise: z.coerce.number().int().positive(),
  servingsCount: z.coerce.number().int().positive(),
});
type VariantForm = z.infer<typeof variantSchema>;

export default function ProductEditorPage() {
  const navigate = useNavigate();
  const [product, setProduct] = useState<ProductView | null>(null);
  const [variants, setVariants] = useState<Array<{ id: number; sku?: string; name?: string }>>([]);

  const productForm = useForm<CreateProductValues>({
    resolver: zodResolver(createProductSchema),
    defaultValues: {
      slug: "",
      name: "",
      line: "SHAKTI",
      shortDescription: "",
      longDescription: "",
      classicalReference: "",
      subscribable: true,
      trialPack: false,
    },
  });

  const variantForm = useForm<VariantForm>({
    resolver: zodResolver(variantSchema),
    defaultValues: {
      sku: "",
      name: "",
      priceMrpPaise: 0,
      priceSellingPaise: 0,
      priceSubscriptionPaise: 0,
      servingsCount: 60,
    },
  });

  const create = useMutation({
    mutationFn: (data: CreateProductValues) => createProduct(data),
    onSuccess: (p) => {
      setProduct(p);
      toast.success(`Draft created: ${p.name}`);
    },
    onError: (e: Error) => toast.error(e.message || "Couldn't create product"),
  });

  const addVariant = useMutation({
    mutationFn: (data: VariantForm) =>
      apiClient.request<{ id: number; sku?: string; name?: string }>(
        `/admin/api/v1/catalog/products/${product?.id}/variants`,
        { method: "POST", body: data },
      ),
    onSuccess: (v) => {
      setVariants((prev) => [...prev, v]);
      variantForm.reset();
      toast.success(`Variant added: ${v.sku}`);
    },
    onError: (e: Error) => toast.error(e.message || "Couldn't add variant"),
  });

  const publish = useMutation({
    mutationFn: () =>
      apiClient.request<void>(`/admin/api/v1/catalog/products/${product?.id}/publish`, {
        method: "POST",
      }),
    onSuccess: () => {
      toast.success("Published");
      navigate("/catalog");
    },
    onError: (e: Error) =>
      toast.error(e.message || "Publish gate failed — variant, image, and lab report required"),
  });

  const locked = !!product;

  return (
    <div className="max-w-3xl space-y-6">
      <header>
        <h1 className="font-display text-display-xl text-charcoal">New product</h1>
        <p className="text-body text-ink">
          Create a draft, add variants, then publish (gate: variant + image + lab report).
        </p>
      </header>

      <Card>
        <h2 className="font-display text-display-sm text-charcoal">1. Product draft</h2>
        <form
          className="mt-4 grid gap-3 sm:grid-cols-2"
          onSubmit={productForm.handleSubmit((d) => create.mutate(d))}
        >
          {/* Create-only, immutable-after-create fields. */}
          <div>
            <label className="text-body-sm text-ink" htmlFor="slug">
              Slug
            </label>
            <Input
              id="slug"
              placeholder="slug-like-this"
              disabled={locked}
              {...productForm.register("slug")}
              invalid={!!productForm.formState.errors.slug}
            />
            {productForm.formState.errors.slug ? (
              <p className="text-caption text-terracotta mt-1">
                {productForm.formState.errors.slug.message}
              </p>
            ) : null}
          </div>
          <div>
            <label className="text-body-sm text-ink" htmlFor="line">
              Line
            </label>
            <Select id="line" disabled={locked} {...productForm.register("line")}>
              {["SHAKTI", "SATTVA", "AMRITA", "AGNI", "TRIAL"].map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </Select>
          </div>

          {/* Shared body fields (also used by Edit). */}
          <ProductFields
            register={productForm.register}
            errors={productForm.formState.errors}
            disabled={locked}
          />

          <label className="text-body-sm text-ink flex items-center gap-2 sm:col-span-2">
            <input type="checkbox" disabled={locked} {...productForm.register("trialPack")} />
            Trial pack
          </label>

          {!locked ? (
            <div className="sm:col-span-2">
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? "Creating…" : "Create draft"}
              </Button>
            </div>
          ) : (
            <p className="text-body-sm text-forest sm:col-span-2">✓ Draft #{product.id} created</p>
          )}
        </form>
      </Card>

      {product ? (
        <Card>
          <h2 className="font-display text-display-sm text-charcoal">2. Variants</h2>
          {variants.length ? (
            <ul className="text-body-sm text-ink mt-2 list-disc pl-5">
              {variants.map((v) => (
                <li key={v.id}>
                  {v.sku} — {v.name}
                </li>
              ))}
            </ul>
          ) : null}
          <form
            className="mt-4 grid gap-3 sm:grid-cols-2"
            onSubmit={variantForm.handleSubmit((d) => addVariant.mutate(d))}
          >
            <div>
              <Input
                placeholder="SKU (NUT-SHK-002)"
                {...variantForm.register("sku")}
                invalid={!!variantForm.formState.errors.sku}
              />
              {variantForm.formState.errors.sku ? (
                <p className="text-caption text-terracotta mt-1">
                  {variantForm.formState.errors.sku.message}
                </p>
              ) : null}
            </div>
            <Input
              placeholder="Variant name (60 caps)"
              {...variantForm.register("name")}
              invalid={!!variantForm.formState.errors.name}
            />
            <Input
              type="number"
              placeholder="MRP (paise)"
              {...variantForm.register("priceMrpPaise")}
              invalid={!!variantForm.formState.errors.priceMrpPaise}
            />
            <Input
              type="number"
              placeholder="Selling price (paise)"
              {...variantForm.register("priceSellingPaise")}
              invalid={!!variantForm.formState.errors.priceSellingPaise}
            />
            <Input
              type="number"
              placeholder="Subscription price (paise)"
              {...variantForm.register("priceSubscriptionPaise")}
              invalid={!!variantForm.formState.errors.priceSubscriptionPaise}
            />
            <Input
              type="number"
              placeholder="Servings"
              {...variantForm.register("servingsCount")}
              invalid={!!variantForm.formState.errors.servingsCount}
            />
            <div className="sm:col-span-2">
              <Button type="submit" variant="outline" disabled={addVariant.isPending}>
                {addVariant.isPending ? "Adding…" : "+ Add variant"}
              </Button>
            </div>
          </form>
        </Card>
      ) : null}

      {product ? (
        <Card>
          <h2 className="font-display text-display-sm text-charcoal">3. Publish</h2>
          <p className="text-body-sm text-ink mt-2">
            The backend enforces the publish gate: at least one variant, one image, and a published
            lab report batch. Media + lab reports attach via the batch workflow.
          </p>
          <Button
            className="mt-3"
            onClick={() => publish.mutate()}
            disabled={publish.isPending || variants.length === 0}
          >
            {publish.isPending ? "Publishing…" : "Publish product"}
          </Button>
        </Card>
      ) : null}
    </div>
  );
}
